import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import FuelCard from './FuelCard';
import FeedbackCard from './FeedbackCard';
import TrainingBandsCard from './TrainingBandsCard';
import PaceBandsCard from './PaceBandsCard';
import ConfigModal from './ConfigModal';
import RecoveryCard from './RecoveryCard';
import NextWorkoutCard from './NextWorkoutCard';
import LatestRunCard from './LatestRunCard';
import ScheduledRunsCard from './ScheduledRunsCard';
import SortableCard from './SortableCard';
import useCardPrefs from './useCardPrefs';
import { latestInspirationRunKey } from './inspirationRunKey';
import T from './theme';
import { buildWeatherChartData, dniAxisMax, dniFromRow } from './weatherChart';
import {
  parsePace, formatPace, formatMiles, formatDuration, formatRunPace,
  extractError, optionalNumber, toDatetimeLocalValue, upcomingSchedule,
} from './utils';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const LOADING_PHRASES = [
  'Lacing up…',
  'Stretching…',
  'Checking the weather…',
  'Warming up…',
  'Finding your pace…',
];

function LoadingSplash() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % LOADING_PHRASES.length), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <p key={idx} className="loading-phrase" style={{ color: T.color.textLight, margin: '24px 0', fontSize: T.font.lg }}>
      {LOADING_PHRASES[idx]}
    </p>
  );
}

const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const API_BASE = process.env.REACT_APP_API_BASE
  || (isLocalHost ? 'http://localhost:8000' : '/api');

function consumeAuthHash() {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw) {
    return {
      token: localStorage.getItem('token'),
      refresh: localStorage.getItem('refresh_token'),
      error: null,
      fromOAuth: false,
    };
  }
  const params = new URLSearchParams(raw);
  const access = params.get('access_token');
  const refresh = params.get('refresh_token');
  const error = params.get('error');
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  if (access) {
    localStorage.setItem('token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
    return { token: access, refresh: refresh || localStorage.getItem('refresh_token'), error: null, fromOAuth: true };
  }
  return {
    token: localStorage.getItem('token'),
    refresh: localStorage.getItem('refresh_token'),
    error: error || null,
    fromOAuth: false,
  };
}

const initialAuth = consumeAuthHash();

const STRAVA_CONNECTED_KEY = 'umax_strava_connected';
const STRAVA_SCOPES_KEY = 'umax_strava_scopes';

function cacheStrava(connected, scopes) {
  localStorage.setItem(STRAVA_CONNECTED_KEY, connected ? '1' : '0');
  localStorage.setItem(STRAVA_SCOPES_KEY, scopes || '');
}

function clearStravaCache() {
  localStorage.removeItem(STRAVA_CONNECTED_KEY);
  localStorage.removeItem(STRAVA_SCOPES_KEY);
}

function paceBandsForDay(paceData) {
  const buckets = { Morning: [], Afternoon: [], Evening: [] };
  for (const d of paceData) {
    if (d.adjusted_pace == null) continue;
    const hour = new Date(d.date).getHours();
    if (hour < 11) buckets.Morning.push(d.adjusted_pace);
    else if (hour < 16) buckets.Afternoon.push(d.adjusted_pace);
    else buckets.Evening.push(d.adjusted_pace);
  }
  const quantile = (xs, p) => {
    const sorted = [...xs].sort((a, b) => a - b);
    if (!sorted.length) return null;
    const i = Math.min(sorted.length - 1, Math.max(0, Math.round(p * (sorted.length - 1))));
    return sorted[i];
  };
  return Object.entries(buckets)
    .filter(([, xs]) => xs.length)
    .map(([name, xs]) => ({
      name,
      low: quantile(xs, 0.2),
      high: quantile(xs, 0.8),
    }));
}

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshInFlight = null;

// Any authed call that ends in a dead session (401 with no refresh, or a failed
// refresh) tells App to show the sign-in screen, instead of the card quietly
// going blank.
export const SESSION_EXPIRED_EVENT = 'umax:session-expired';
export const SESSION_EXPIRED_MESSAGE = 'Session expired. Sign in with Strava to continue.';

const announceSessionExpired = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';
    const skip = url.includes('/login') || url.includes('/auth/refresh') || url.includes('/register');
    if (status !== 401 || original._retry || skip) {
      throw error;
    }
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      announceSessionExpired();
      throw error;
    }
    original._retry = true;
    if (!refreshInFlight) {
      refreshInFlight = axios
        .post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken })
        .then((res) => {
          localStorage.setItem('token', res.data.access_token);
          if (res.data.refresh_token) {
            localStorage.setItem('refresh_token', res.data.refresh_token);
          }
          return res.data.access_token;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    try {
      const newToken = await refreshInFlight;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      announceSessionExpired();
      throw refreshErr;
    }
  }
);

function App() {
  const [weatherData, setWeatherData] = useState([]);
  const [paceData, setPaceData] = useState([]);
  const [runs, setRuns] = useState([]);
  const [basePace, setBasePace] = useState(8.0);
  const [basePaceInput, setBasePaceInput] = useState('8:00');
  const [error, setError] = useState(initialAuth.error);
  const [token, setToken] = useState(initialAuth.token);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [stravaConnected, setStravaConnected] = useState(
    () => localStorage.getItem(STRAVA_CONNECTED_KEY) === '1'
  );
  const [stravaScopes, setStravaScopes] = useState(
    () => localStorage.getItem(STRAVA_SCOPES_KEY) || ''
  );
  const [withingsConnected, setWithingsConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(() => Boolean(initialAuth.token));
  const lastRefreshAt = useRef(0);
  const refreshingRef = useRef(false);
  const scheduleAtRef = useRef('');
  const seededWhenRef = useRef(false);
  const whenTimerRef = useRef(null);
  const inspirationRunKeyRef = useRef(null);
  const [showPaceTable, setShowPaceTable] = useState(false);
  const [showWeatherChart, setShowWeatherChart] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [planStarts, setPlanStarts] = useState('');
  const [planMinutes, setPlanMinutes] = useState('60');
  const [planKind, setPlanKind] = useState('long');
  const [planTitle, setPlanTitle] = useState('');
  const [partners, setPartners] = useState([]);
  const [inviteEvent, setInviteEvent] = useState(null);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [ageInput, setAgeInput] = useState('');
  const [hrMaxInput, setHrMaxInput] = useState('');
  const [hrRestInput, setHrRestInput] = useState('');
  const [vo2Input, setVo2Input] = useState('');
  const [lthrInput, setLthrInput] = useState('');
  const [goalEvent, setGoalEvent] = useState('general');
  const [trainingAppetite, setTrainingAppetite] = useState('');
  const [massKg, setMassKg] = useState(70);
  const [gutTolerance, setGutTolerance] = useState('med');
  const [lossRate, setLossRate] = useState(0);
  const [settingsVersion, setSettingsVersion] = useState(0);
  const [trainingBands, setTrainingBands] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [nextWorkout, setNextWorkout] = useState(null);
  const [workoutChoice, setWorkoutChoice] = useState(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [sleepScoreInput, setSleepScoreInput] = useState('');
  const [inspiration, setInspiration] = useState('');
  const [checkinInput, setCheckinInput] = useState('');
  const [checkinReply, setCheckinReply] = useState('');
  const [checkinBusy, setCheckinBusy] = useState(false);

  const { order, setOrder, isCollapsed, toggleCollapsed } = useCardPrefs();
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrder((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
    }
  }, [setOrder]);

  const applyAuth = (access, refresh, savedPace) => {
    localStorage.setItem('token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
    if (typeof savedPace === 'number' && savedPace > 0) {
      setBasePace(savedPace);
      setBasePaceInput(formatPace(savedPace));
    }
    setToken(access);
    setError(null);
  };

  const handleAuth = async () => {
    try {
      if (isLogin) {
        const form = new URLSearchParams();
        form.append('username', email);
        form.append('password', password);
        const response = await axios.post(`${API_BASE}/login`, form, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        const { access_token, refresh_token, base_pace: savedPace } = response.data;
        applyAuth(access_token, refresh_token, savedPace);
      } else {
        const parsedPace = parsePace(basePaceInput);
        const payload = {
          email,
          password,
          location_lat: 39.1031,
          location_lon: -84.512,
          base_pace: parsedPace != null ? parsedPace : 8.0,
        };

        try {
          await axios.post(`${API_BASE}/register`, payload, {
            headers: { 'Content-Type': 'application/json' },
          });
        } catch (err) {
          if (err.response?.status === 422 || err.response?.status === 405) {
            await axios.post(`${API_BASE}/register`, null, { params: payload });
          } else {
            throw err;
          }
        }

        setError('Registration successful! Please log in.');
        setIsLogin(true);
      }
    } catch (err) {
      setError(`Failed to ${isLogin ? 'login' : 'register'}: ${extractError(err)}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    clearStravaCache();
    setToken(null);
    setWeatherData([]);
    setPaceData([]);
    setRuns([]);
    setRecovery(null);
    setTrainingBands(null);
    setLastRun(null);
    setNextWorkout(null);
    setWorkoutChoice(null);
    setScheduleAt('');
    scheduleAtRef.current = '';
    seededWhenRef.current = false;
    setSchedule([]);
    setPartners([]);
    setInviteEvent(null);
    setMyWorkouts([]);
    setStravaConnected(false);
    setStravaScopes('');
    setBootstrapping(false);
    setSyncing(false);
    inspirationRunKeyRef.current = null;
    setInspiration('');
    setCheckinReply('');
    setCheckinInput('');
    setGoalEvent('general');
    setTrainingAppetite('');
  };

  const handleWithings = async () => {
    const current = localStorage.getItem('token');
    if (!current) {
      setError('Log in before linking Withings.');
      return;
    }
    window.location.href = `${API_BASE}/auth/withings/login?access_token=${encodeURIComponent(current)}`;
  };

  const handleStrava = async () => {
    try {
      await axios.get(`${API_BASE}/health`, { timeout: 4000 });
    } catch {
      setError(
        isLocalHost
          ? 'UMAX API is not running on localhost:8000. Start it, or use https://umax.amdgtechnologies.com to sign in with Strava.'
          : 'UMAX API is not reachable. Try again in a moment.'
      );
      return;
    }
    const current = localStorage.getItem('token');
    if (current) {
      window.location.href = `${API_BASE}/auth/strava/login?access_token=${encodeURIComponent(current)}`;
    } else {
      window.location.href = `${API_BASE}/auth/strava/login`;
    }
  };

  const fetchData = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const weatherResponse = await api.get('/weather');
      setWeatherData(weatherResponse.data);
      const paceResponse = await api.get('/adjusted_pace');
      setPaceData(paceResponse.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setError(SESSION_EXPIRED_MESSAGE);
        return;
      }
      setError('Failed to fetch data: ' + extractError(err));
    }
  }, []);

  const fetchRecovery = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await api.get('/me/recovery');
      setRecovery(response.data);
    } catch (err) {
      if (err.response?.status !== 401) {
        setRecovery(null);
      }
    }
  }, []);

  const fetchTrainingBands = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await api.get('/me/training-bands');
      setTrainingBands(response.data);
      const easy = response.data?.easy_pace;
      if (typeof easy === 'number' && easy > 0) {
        setBasePace(easy);
        setBasePaceInput(formatPace(easy));
      }
    } catch (err) {
      if (err.response?.status !== 401) {
        setTrainingBands(null);
      }
    }
  }, []);

  const fetchNextWorkout = useCallback(async (atValue) => {
    if (!localStorage.getItem('token')) return;
    const raw = atValue === undefined ? scheduleAtRef.current : atValue;
    const params = {};
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) params.at = d.toISOString();
    }
    try {
      const response = await api.get('/me/next-workout', { params });
      setNextWorkout(response.data);
      setWorkoutChoice((prev) => {
        const rungs = response.data?.rungs || [];
        if (prev && rungs.some((w) => (w.rung || w.id) === prev)) return prev;
        return null;
      });
    } catch (err) {
      if (err.response?.status !== 401) {
        setNextWorkout(null);
      }
    }
  }, []);

  const handleRecover = async () => {
    try {
      await api.post('/me/recover');
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
      setError(null);
    } catch (err) {
      setError('Failed to mark recovered: ' + extractError(err));
    }
  };

  const fetchSchedule = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await api.get('/me/schedule');
      setSchedule(upcomingSchedule(response.data || []));
    } catch (err) {
      if (err.response?.status !== 401) {
        setError('Failed to load schedule: ' + extractError(err));
      }
    }
  }, []);

  const fetchWorkouts = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await api.get('/me/workouts');
      setMyWorkouts(response.data || []);
    } catch (err) {
      if (err.response?.status !== 401) {
        setMyWorkouts([]);
      }
    }
  }, []);

  const fetchInspiration = useCallback(async () => {
    try {
      const req = localStorage.getItem('token')
        ? api.get('/inspiration')
        : axios.get(`${API_BASE}/inspiration`);
      const res = await req;
      if (res.data?.phrase) setInspiration(res.data.phrase);
    } catch {
      /* garnish — keep the last line if Grok is briefly unhappy */
    }
  }, []);

  const applyDashboardData = useCallback((data) => {
    if (!data) return;
    setWeatherData(data.weather || []);
    setPaceData(data.adjusted_pace || []);
    setError(null);
    setRuns(data.runs || []);
    setRecovery(data.recovery || null);
    setLastRun(data.last_run || null);
    setSchedule(upcomingSchedule(data.schedule || []));
    setPartners(data.partners || []);
    setMyWorkouts(data.workouts || []);
    if (data.checkin?.reply) setCheckinReply(data.checkin.reply);
    setNextWorkout(data.next_workout || null);
    setWorkoutChoice(null);
    if (data.training_bands) {
      setTrainingBands(data.training_bands);
      const easy = data.training_bands?.easy_pace;
      if (typeof easy === 'number' && easy > 0) {
        setBasePace(easy);
        setBasePaceInput(formatPace(easy));
      }
    }
    const key = latestInspirationRunKey(data.runs);
    if (inspirationRunKeyRef.current !== key) {
      inspirationRunKeyRef.current = key;
      void fetchInspiration();
    }
  }, [fetchInspiration]);

  const fetchDashboard = useCallback(async (atValue) => {
    if (!localStorage.getItem('token')) return null;
    const raw = atValue === undefined ? scheduleAtRef.current : atValue;
    const params = {};
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) params.at = d.toISOString();
    }
    try {
      const response = await api.get('/me/dashboard', { params });
      applyDashboardData(response.data);
      return response.data;
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setError('Failed to load: ' + extractError(err));
      }
      return null;
    }
  }, [applyDashboardData]);

  const refreshDashboard = useCallback(async (opts = {}) => {
    if (!localStorage.getItem('token')) return;
    const force = Boolean(opts.force);
    const now = Date.now();
    if (refreshingRef.current) return;
    if (!force && now - lastRefreshAt.current < 15000) return;
    refreshingRef.current = true;
    lastRefreshAt.current = now;
    setSyncing(true);
    try {
      // Fresh numbers first; the Strava sync (an outside call) follows, and the
      // dashboard re-loads only if it brought something new.
      await fetchDashboard();
      if (stravaConnected && String(stravaScopes).includes('activity:read_all')) {
        try {
          const syncRes = await api.post('/strava/sync');
          const sd = syncRes.data || {};
          if (Number(sd.imported) > 0 || Number(sd.updated) > 0) {
            await fetchDashboard();
          }
        } catch (err) {
          if (err.response?.status !== 403 && err.response?.status !== 401) {
            /* keep the page usable if Strava is briefly unhappy */
          }
        }
      }
    } finally {
      refreshingRef.current = false;
      setSyncing(false);
      lastRefreshAt.current = Date.now();
    }
  }, [stravaConnected, stravaScopes, fetchDashboard]);

  const handleSaveRecommendedWorkout = async (workout) => {
    if (!workout?.steps?.length) return;
    try {
      await api.post('/me/workouts', {
        name: workout.name,
        kind: workout.kind === 'rest' ? 'easy' : (workout.kind || 'workout'),
        notes: workout.recipe,
        steps: workout.steps,
      });
      await fetchWorkouts();
      setError(null);
    } catch (err) {
      setError('Failed to save workout: ' + extractError(err));
    }
  };

  const handleScheduleSavedWorkout = async (workout) => {
    if (!planStarts) {
      setError('Pick a date and time above, then schedule this workout.');
      return;
    }
    const starts = new Date(planStarts);
    if (Number.isNaN(starts.getTime())) {
      setError('Scheduled time is invalid.');
      return;
    }
    try {
      await api.post('/me/schedule', {
        starts_at: starts.toISOString(),
        title: workout.name,
        planned_minutes: workout.duration_min,
        kind: workout.kind === 'rest' ? 'easy' : (workout.kind || 'workout'),
        notes: workout.notes,
        steps: workout.steps,
      });
      await fetchSchedule();
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
      setError(null);
    } catch (err) {
      setError('Failed to schedule workout: ' + extractError(err));
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    try {
      await api.delete(`/me/workouts/${workoutId}`);
      await fetchWorkouts();
    } catch (err) {
      setError('Failed to delete workout: ' + extractError(err));
    }
  };

  const handleAddScheduled = async (e) => {
    e.preventDefault();
    if (!planStarts) {
      setError('Pick a date and time for the scheduled run.');
      return;
    }
    const starts = new Date(planStarts);
    if (Number.isNaN(starts.getTime())) {
      setError('Scheduled time is invalid.');
      return;
    }
    try {
      await api.post('/me/schedule', {
        starts_at: starts.toISOString(),
        title: planTitle || undefined,
        planned_minutes: planMinutes ? Number(planMinutes) : undefined,
        kind: planKind,
      });
      setPlanTitle('');
      await fetchSchedule();
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
      setError(null);
    } catch (err) {
      setError('Failed to save scheduled run: ' + extractError(err));
    }
  };

  const handleDeleteScheduled = async (eventId) => {
    try {
      await api.delete(`/me/schedule/${eventId}`);
      await fetchSchedule();
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
    } catch (err) {
      setError('Failed to remove scheduled run: ' + extractError(err));
    }
  };

  const handleInviteUser = async (userId) => {
    if (!inviteEvent || !userId) return;
    try {
      await api.post(`/me/schedule/${inviteEvent.id}/invite`, { user_id: userId });
      setInviteEvent(null);
      await fetchSchedule();
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
      setError(null);
    } catch (err) {
      setError('Failed to invite: ' + extractError(err));
    }
  };

  const handleScheduleWorkout = async (workout) => {
    if (!workout || workout.id === 'rest' || !workout.duration_min) return;
    const raw = scheduleAt || toDatetimeLocalValue(nextWorkout?.ready_by);
    if (!raw) {
      setError('Pick a date and time to schedule this.');
      return;
    }
    const starts = new Date(raw);
    if (Number.isNaN(starts.getTime())) {
      setError('Scheduled time is invalid.');
      return;
    }
    try {
      await api.post('/me/schedule', {
        starts_at: starts.toISOString(),
        title: workout.name,
        planned_minutes: workout.duration_min,
        kind: workout.kind === 'rest' ? 'easy' : workout.kind,
        notes: workout.recipe,
        workout_id: workout.id,
        steps: workout.steps,
      });
      await fetchSchedule();
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
      setError(null);
    } catch (err) {
      setError('Failed to schedule workout: ' + extractError(err));
    }
  };

  const handleSaveDraftWorkout = async (name, steps) => {
    try {
      await api.post('/me/workouts', { name, steps, kind: 'workout' });
      await fetchWorkouts();
      setError(null);
    } catch (err) {
      setError('Failed to save workout: ' + extractError(err));
    }
  };

  const handleScheduleDraftWorkout = async (name, steps) => {
    if (!planStarts) {
      setError('Pick a date and time above, then schedule this workout.');
      return;
    }
    const starts = new Date(planStarts);
    if (Number.isNaN(starts.getTime())) {
      setError('Scheduled time is invalid.');
      return;
    }
    const minutes = steps.reduce((sum, s) => sum + s.duration_min, 0);
    try {
      await api.post('/me/workouts', { name, steps, kind: 'workout' });
      await api.post('/me/schedule', {
        starts_at: starts.toISOString(),
        title: name,
        planned_minutes: minutes,
        kind: 'workout',
        steps,
      });
      await fetchWorkouts();
      await fetchSchedule();
      await Promise.all([fetchRecovery(), fetchNextWorkout()]);
      setError(null);
    } catch (err) {
      setError('Failed to schedule workout: ' + extractError(err));
    }
  };

  useEffect(() => {
    // handleLogout only calls setters, so the first-render closure is safe.
    const onExpired = () => {
      handleLogout();
      setError(SESSION_EXPIRED_MESSAGE);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scheduleAtRef.current = scheduleAt;
  }, [scheduleAt]);

  useEffect(() => {
    if (seededWhenRef.current || !nextWorkout) return;
    const local = nextWorkout.ran_today
      ? toDatetimeLocalValue(nextWorkout.ready_by)
      : toDatetimeLocalValue(new Date().toISOString());
    if (!local) return;
    setScheduleAt(local);
    scheduleAtRef.current = local;
    seededWhenRef.current = true;
  }, [nextWorkout]);

  useEffect(() => {
    if (!token) {
      setBootstrapping(false);
      return;
    }
    let cancelled = false;
    let backgroundStarted = false;

    (async () => {
      lastRefreshAt.current = Date.now();
      setSyncing(true);
      setBootstrapping(true);
      try {
        // Profile and dashboard do not depend on each other: load both at once.
        const dashboardP = fetchDashboard();
        const me = await api.get('/me');
        if (cancelled) return;
        const data = me.data || {};
        const savedEasy = data.easy_pace;
        const saved = typeof savedEasy === 'number' && savedEasy > 0 ? savedEasy : data.base_pace;
        if (typeof saved === 'number' && saved > 0) {
          setBasePace(saved);
          setBasePaceInput(formatPace(saved));
        }
        const connected = Boolean(data.strava_connected);
        const scopes = data.strava_scopes || '';
        setStravaConnected(connected);
        setStravaScopes(scopes);
        cacheStrava(connected, scopes);
        setWithingsConnected(Boolean(data.withings_connected));
        setAgeInput(data.age != null ? String(data.age) : '');
        setHrMaxInput(data.hr_max != null ? String(data.hr_max) : '');
        setHrRestInput(data.hr_rest != null ? String(data.hr_rest) : '');
        setVo2Input(data.vo2_max != null ? String(data.vo2_max) : '');
        setLthrInput(data.lthr != null ? String(data.lthr) : '');
        setGoalEvent(data.goal_event || 'general');
        setTrainingAppetite(data.training_appetite || '');
        setSleepScoreInput(data.sleep_score != null ? String(data.sleep_score) : '');
        if (data.body_mass_kg) setMassKg(data.body_mass_kg);
        if (data.gut_tolerance) setGutTolerance(data.gut_tolerance);
        setLossRate(Number(data.weight_loss_rate_lb_wk) || 0);

        await dashboardP;
        if (cancelled) return;
        setBootstrapping(false);

        backgroundStarted = true;
        void (async () => {
          try {
            let weatherUpdated = false;
            try {
              const wx = await api.get('/refresh_db');
              weatherUpdated = Boolean(wx.data?.updated);
            } catch {
              /* weather refresh is best-effort */
            }
            let syncChanged = false;
            if (connected && String(scopes).includes('activity:read_all')) {
              try {
                const syncRes = await api.post('/strava/sync');
                const sd = syncRes.data || {};
                syncChanged = Number(sd.imported) > 0 || Number(sd.updated) > 0;
              } catch (err) {
                if (err.response?.status !== 403 && err.response?.status !== 401) {
                  /* keep the page usable if Strava is briefly unhappy */
                }
              }
            }
            if (cancelled) return;
            if (syncChanged || weatherUpdated) {
              await fetchDashboard();
            }
          } finally {
            if (!cancelled) setSyncing(false);
          }
        })();
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          handleLogout();
        } else {
          setError('Failed to load: ' + extractError(err));
          setBootstrapping(false);
        }
      } finally {
        if (!cancelled && !backgroundStarted) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, fetchDashboard]);

  useEffect(() => {
    if (!token) return;
    const onResume = () => {
      if (document.visibilityState === 'hidden') return;
      refreshDashboard();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') onResume();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('focus', onResume);
    let lastBeat = Date.now();
    const tick = window.setInterval(() => {
      const now = Date.now();
      const gap = now - lastBeat;
      lastBeat = now;
      if (gap > 15000) onResume();
    }, 2000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('focus', onResume);
      window.clearInterval(tick);
    };
  }, [token, refreshDashboard]);

  useEffect(() => {
    if (bootstrapping) return;
    if (token) return;
    let cancelled = false;
    axios.get(`${API_BASE}/inspiration`).then((res) => {
      if (!cancelled && res.data?.phrase) setInspiration(res.data.phrase);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, bootstrapping]);

  const handleBasePaceChange = (e) => {
    const input = e.target.value;
    setBasePaceInput(input);
    const parsedPace = parsePace(input);
    if (parsedPace !== null) {
      setBasePace(parsedPace);
      setError(null);
    } else {
      setError('Please enter pace in mm:ss format (e.g., 8:30)');
    }
  };

  const handleSaveConfig = async () => {
    const easyFromRuns = trainingBands && ['easy_runs', 'all_runs', 'recent_runs', 'gap_hr'].includes(trainingBands.easy_pace_source);
    const body = {};
    if (!easyFromRuns) {
      const parsedPace = parsePace(basePaceInput);
      if (parsedPace === null) {
        setError('Please enter pace in mm:ss format (e.g., 8:30)');
        return;
      }
      body.base_pace = parsedPace;
    }
    const age = optionalNumber(ageInput);
    const hrMax = optionalNumber(hrMaxInput);
    const hrRest = optionalNumber(hrRestInput);
    const vo2 = optionalNumber(vo2Input);
    const lthr = optionalNumber(lthrInput);
    if (age != null) body.age = age;
    if (hrMax != null) body.hr_max = hrMax;
    if (hrRest != null) body.hr_rest = hrRest;
    if (vo2 != null) body.vo2_max = vo2;
    if (lthr != null) body.lthr = lthr;
    body.goal_event = goalEvent || 'general';
    body.training_appetite = trainingAppetite || 'default';
    const mass = optionalNumber(massKg);
    if (mass != null) body.body_mass_kg = mass;
    if (gutTolerance) body.gut_tolerance = gutTolerance;
    body.weight_loss_rate_lb_wk = Number(lossRate) || 0;
    try {
      if (token) {
        await api.patch('/me', body);
        await Promise.all([fetchTrainingBands(), fetchData(), fetchRecovery(), fetchNextWorkout()]);
      }
      setSettingsVersion((v) => v + 1);
      setShowConfig(false);
      setError(null);
    } catch (err) {
      setError('Failed to save config: ' + extractError(err));
    }
  };

  const todayBands = paceBandsForDay(paceData);
  const workoutRungs = (nextWorkout?.rungs && nextWorkout.rungs.length)
    ? nextWorkout.rungs
    : (nextWorkout?.primary
      ? [nextWorkout.primary, ...(nextWorkout.alternatives || [])]
      : []);
  const shownWorkout = workoutRungs.find((w) => (w.rung || w.id) === workoutChoice)
    || workoutRungs.find((w) => w.rung === 'recommended')
    || nextWorkout?.primary;
  const paceValues = paceData.map(d => d.adjusted_pace || 0);
  const minPace = paceValues.length > 0 ? Math.min(...paceValues) : basePace;
  const maxPace = paceValues.length > 0 ? Math.max(...paceValues) : basePace;
  const yRightMin = Math.max(0, Math.min(basePace , minPace - 0.25));
  const yRightMax = Math.max(basePace + 0.5, maxPace + 0.25);
  const dniValues = weatherData.map(dniFromRow);
  const yDniMax = dniAxisMax(dniValues);
  const needsStravaReconnect = stravaConnected && !(stravaScopes || '').includes('activity:read_all');

  const chartData = buildWeatherChartData({
    weatherData,
    paceData,
    colors: T.chart,
  });

  const chartOptions = {
    responsive: true,
    scales: {
      'y-left': {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Temperature / Mugginess (°F)' },
        beginAtZero: false,
      },
      'y-right': {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Adjusted Pace (min/mile)' },
        min: yRightMin,
        max: yRightMax,
        grid: { drawOnChartArea: false },
        ticks: { callback: function(value) { return formatPace(value); } },
      },
      'y-dni': {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Solar DNI (W/m²)', color: T.chart.dni },
        min: 0,
        max: yDniMax,
        grid: { drawOnChartArea: false },
        offset: true,
        ticks: { color: T.chart.dni },
      },
    },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Adjusted Pace (min/mile)') {
              return `${context.dataset.label}: ${formatPace(context.raw)}`;
            }
            if (context.dataset.label === 'Solar DNI (W/m²)') {
              if (context.raw == null || !Number.isFinite(Number(context.raw))) {
                return `${context.dataset.label}: —`;
              }
              return `${context.dataset.label}: ${Math.round(Number(context.raw))} W/m²`;
            }
            return `${context.dataset.label}: ${Number(context.raw).toFixed(1)}`;
          },
        },
      },
    },
  };

  const stravaButtonStyle = {
    margin: '10px',
    background: T.color.strava,
    color: 'white',
    border: 'none',
    borderRadius: T.radius.sm,
    padding: '8px 14px',
    cursor: 'pointer',
    fontWeight: 600,
  };

  return (
    <div className="dashboard-root">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, borderBottom: `1px solid ${T.color.borderLight}`, paddingBottom: 12, marginBottom: 8, paddingLeft: 8 }}>
        <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* The Grok line moved onto the Latest run card; the header is title
              and controls only. */}
          <h1 style={{ margin: 0, fontSize: T.font.xxl }}>UMAX Training Dashboard</h1>
        </div>
        {token && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', flexShrink: 0, position: 'relative' }}>
            <button type="button" onClick={() => setShowConfig(true)}>
              Settings
            </button>
            <button type="button" onClick={handleLogout}>
              Logout
            </button>
            {syncing && <span style={{ color: T.color.textLight, position: 'absolute', top: '100%', right: 0, fontSize: 13, whiteSpace: 'nowrap', marginTop: 4 }}>Updating…</span>}
          </div>
        )}
      </div>
      {error && <p style={{ color: T.color.danger }}>{error}</p>}
      {!token ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
          <div className="card auth-card" style={{ ...T.cardStyle, maxWidth: 400, padding: 24 }}>
            <h2 style={{ marginTop: 0 }}>{isLogin ? 'Login' : 'Register'}</h2>
            <button onClick={handleStrava} style={{ ...stravaButtonStyle, margin: '0 0 16px', width: '100%' }}>
              Continue with Strava
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email"
              />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
              />
              <button onClick={handleAuth}>
                {isLogin ? 'Login' : 'Register'}
              </button>
              {!isLogin && (
                <input
                  type="text"
                  value={basePaceInput}
                  onChange={handleBasePaceChange}
                  placeholder="Easy pace mm:ss (optional; Strava will estimate)"
                />
              )}
              <button onClick={() => setIsLogin(!isLogin)} style={T.linkButtonStyle}>
                Switch to {isLogin ? 'Register' : 'Login'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {!bootstrapping && (!stravaConnected || needsStravaReconnect) && (
            <button onClick={handleStrava} style={stravaButtonStyle}>
              {needsStravaReconnect ? 'Reconnect Strava' : 'Connect Strava'}
            </button>
          )}
          {showConfig && (
            <ConfigModal
              api={api}
              goalEvent={goalEvent} setGoalEvent={setGoalEvent}
              trainingAppetite={trainingAppetite} setTrainingAppetite={setTrainingAppetite}
              trainingBands={trainingBands}
              basePaceInput={basePaceInput} onBasePaceChange={handleBasePaceChange}
              ageInput={ageInput} setAgeInput={setAgeInput}
              hrMaxInput={hrMaxInput} setHrMaxInput={setHrMaxInput}
              hrRestInput={hrRestInput} setHrRestInput={setHrRestInput}
              lthrInput={lthrInput} setLthrInput={setLthrInput}
              vo2Input={vo2Input} setVo2Input={setVo2Input}
              massKg={massKg} setMassKg={setMassKg}
              gutTolerance={gutTolerance} setGutTolerance={setGutTolerance}
              lossRate={lossRate} setLossRate={setLossRate}
              withingsConnected={withingsConnected}
              onConnectWithings={handleWithings}
              onSave={handleSaveConfig} onClose={() => setShowConfig(false)}
            />
          )}
          {inviteEvent && (
            <div
              role="dialog"
              aria-label="Invite user"
              onClick={() => setInviteEvent(null)}
              style={T.modalOverlayStyle}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={T.modalBoxStyle}
              >
                <h2 style={{ marginTop: 0 }}>Invite user</h2>
                <p style={{ color: T.color.textMuted, marginTop: 0 }}>
                  {inviteEvent.title || 'Scheduled run'}
                  {inviteEvent.starts_at_local ? ` · ${inviteEvent.starts_at_local}` : ''}
                  . They'll run at your paces; recovery load is estimated for them.
                </p>
                {(partners.filter((p) => !(inviteEvent.with || []).some((w) => w.id === p.id))).length === 0 ? (
                  <p style={{ color: T.color.textLight }}>Everyone is already on this run.</p>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                    {partners
                      .filter((p) => !(inviteEvent.with || []).some((w) => w.id === p.id))
                      .map((p) => (
                        <li key={p.id} style={{ marginBottom: 8 }}>
                          <button type="button" onClick={() => handleInviteUser(p.id)}>
                            {p.display_name}
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
                <button type="button" onClick={() => setInviteEvent(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {bootstrapping ? (
            <LoadingSplash />
          ) : (
          <>
          {(() => {
            const CARDS = {
              'latest-run': <LatestRunCard lastRun={lastRun} inspiration={inspiration} />,
              'pace-bands': <PaceBandsCard trainingBands={trainingBands} todayBands={todayBands} />,
              'next-workout': (
                <NextWorkoutCard
                  nextWorkout={nextWorkout}
                  shownWorkout={shownWorkout}
                  workoutRungs={workoutRungs}
                  workoutChoice={workoutChoice}
                  setWorkoutChoice={setWorkoutChoice}
                  scheduleAt={scheduleAt}
                  setScheduleAt={setScheduleAt}
                  scheduleAtRef={scheduleAtRef}
                  whenTimerRef={whenTimerRef}
                  onScheduleWorkout={handleScheduleWorkout}
                  onSaveRecommendedWorkout={handleSaveRecommendedWorkout}
                  onFetchNextWorkout={fetchNextWorkout}
                />
              ),
              'fuel': (
                <FuelCard
                  api={api}
                  weatherData={weatherData}
                  defaultMass={Number(massKg) || 70}
                  defaultGut={gutTolerance}
                  refreshKey={settingsVersion}
                />
              ),
              'recovery': (
                <RecoveryCard
                  recovery={recovery}
                  nextWorkout={nextWorkout}
                  todayBands={todayBands}
                  checkinInput={checkinInput}
                  setCheckinInput={setCheckinInput}
                  checkinReply={checkinReply}
                  setCheckinReply={setCheckinReply}
                  checkinBusy={checkinBusy}
                  setCheckinBusy={setCheckinBusy}
                  sleepScoreInput={sleepScoreInput}
                  setSleepScoreInput={setSleepScoreInput}
                  onRecover={handleRecover}
                  api={api}
                  setError={setError}
                  fetchRecovery={fetchRecovery}
                  fetchNextWorkout={fetchNextWorkout}
                />
              ),
              'training-bands': <TrainingBandsCard trainingBands={trainingBands} />,
              'scheduled-runs': (
                <ScheduledRunsCard
                  schedule={schedule}
                  myWorkouts={myWorkouts}
                  partners={partners}
                  trainingBands={trainingBands}
                  planStarts={planStarts} setPlanStarts={setPlanStarts}
                  planMinutes={planMinutes} setPlanMinutes={setPlanMinutes}
                  planKind={planKind} setPlanKind={setPlanKind}
                  planTitle={planTitle} setPlanTitle={setPlanTitle}
                  onAddScheduled={handleAddScheduled}
                  onDeleteScheduled={handleDeleteScheduled}
                  onScheduleSavedWorkout={handleScheduleSavedWorkout}
                  onDeleteWorkout={handleDeleteWorkout}
                  onSaveDraftWorkout={handleSaveDraftWorkout}
                  onScheduleDraftWorkout={handleScheduleDraftWorkout}
                  onInviteEvent={setInviteEvent}
                  setError={setError}
                />
              ),
              'feedback': <FeedbackCard api={api} setError={setError} />,
            };
            return (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                  {order.map((id) => (
                    <SortableCard key={id} id={id} isOpen={!isCollapsed(id)} onToggle={() => toggleCollapsed(id)}>
                      {CARDS[id]}
                    </SortableCard>
                  ))}
                </SortableContext>
              </DndContext>
            );
          })()}

          <p>
            <button type="button" onClick={() => setShowWeatherChart((open) => !open)} style={T.linkButtonStyle}>
              {showWeatherChart ? 'Hide weather chart' : 'Show weather chart'}
            </button>
            {' · '}
            <button type="button" onClick={() => setShowPaceTable((open) => !open)} style={T.linkButtonStyle}>
              {showPaceTable ? 'Hide hourly pace table' : 'Show hourly pace table'}
            </button>
            {' · '}
            <button type="button" onClick={() => setShowRuns((open) => !open)} style={T.linkButtonStyle}>
              {showRuns ? 'Hide recent runs' : 'Show recent runs'}
            </button>
          </p>

          {showWeatherChart && (
            paceData.length > 0 ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <p>No pace data available</p>
            )
          )}
          {showPaceTable && paceData.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Adjusted Pace (min/mile)</th>
                </tr>
              </thead>
              <tbody>
                {paceData.map((d, i) => (
                  <tr key={i}>
                    <td>{new Date(d.date).toLocaleString()}</td>
                    <td>{formatPace(d.adjusted_pace)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {showRuns && (
            runs.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Distance (mi)</th>
                    <th>Moving time</th>
                    <th>Avg pace</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.id || run.strava_activity_id}>
                      <td>
                        {run.start_date ? new Date(run.start_date).toLocaleString() : '—'}
                      </td>
                      <td>{run.name || run.sport_type}</td>
                      <td>{formatMiles(run.distance_m)}</td>
                      <td>{formatDuration(run.moving_time_s)}</td>
                      <td>
                        {formatRunPace(run)}
                      </td>
                      <td>
                        {run.workout_type_label || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>{stravaConnected ? 'No imported runs yet. Sync Strava to pull the last 90 days.' : 'Connect Strava to import runs.'}</p>
            )
          )}
          </>
          )}
        </>
      )}
    </div>
  );
}

export default App;
